import { WorkPage } from '../components/work-page.tsx';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { photoQuoteApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { companyHasPlanFeature } from '../components/plan-gate.tsx';
import { Alert, LoadingBlock } from '../components/ui.tsx';
import { PhotoGrid, RiskBadge, homeLine, money } from '../components/photo-quotes/parts.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { formatMinutes } from '../i18n/format.ts';
import { activeLanguage } from '../i18n/index.ts';
import { PLANS } from '@shared/constants/index.ts';
import type { RoomFindingValue } from '@shared/photo-quotes.ts';

/**
 * The sample house (spec WILD-05, Flow D): what a photo quote looks like, on
 * any plan, before a company has a customer who sent one.
 *
 * The photos are drawn, the read is recorded, and nothing calls a model or
 * spends a credit. The price is the company's own, though — its library and
 * its rates through the same engine a real one uses — so the number is the
 * one they would have sent. The upgrade lines name the cheapest plan that
 * collects photos first and the one that reads them second.
 */
export default function PhotoQuoteDemoPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('photoQuotes.demo.documentTitle'));
  const { company, user } = useSession();
  const language = activeLanguage() === 'es' ? 'es' : 'en';

  const query = useQuery({
    queryKey: ['photo-quote-demo', language],
    queryFn: () => photoQuoteApi.demo(language),
    enabled: company?.photoQuotesEnabled !== false,
  });

  if (company && !company.photoQuotesEnabled) return <Navigate to="/quotes" replace />;

  const demo = query.data;
  const hasIntake = companyHasPlanFeature(company, 'online_intake');
  const hasRead = companyHasPlanFeature(company, 'magic_quote');

  return (
    <WorkPage
      title={t('photoQuotes.demo.title')}
      description={t('photoQuotes.demo.description')}
      actions={
        <Link to={hasRead ? '/quotes/photos' : '/quotes'} className="btn btn-secondary">
          {t('photoQuotes.demo.back')}
        </Link>
      }
    >
      <div className="mb-4">
        <Alert tone="info" title={t('photoQuotes.demo.sampleTitle')}>
          <p>{t('photoQuotes.demo.sampleBody')}</p>
          {demo?.samplePrices ? <p className="mt-1">{t('photoQuotes.demo.samplePrices')}</p> : null}
        </Alert>
      </div>

      {query.isLoading ? <LoadingBlock label={t('photoQuotes.demo.loading')} /> : null}
      {query.isError ? (
        <Alert tone="error" title={t('photoQuotes.demo.loadErrorTitle')}>
          {(query.error as Error).message}
        </Alert>
      ) : null}

      {demo ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <section className="card">
            <h2 className="mb-3 text-lg font-semibold">{t('photoQuotes.demo.photosHeading')}</h2>
            <PhotoGrid
              photos={demo.photos.map((photo) => ({
                id: String(photo.position),
                src: photo.src,
                label: `${t('photoQuotes.photos.label', { position: photo.position })} · ${photo.caption}`,
                hidden: photo.hiddenReason !== null,
                hiddenReason: photo.hiddenReason,
              }))}
            />
            <p className="mt-3 text-sm text-[color:var(--color-muted)]">{homeLine(t, demo.home)}</p>
          </section>

          <section className="space-y-6">
            <div className="card">
              <h2 className="mb-2 text-lg font-semibold">{t('photoQuotes.review.readHeading')}</h2>
              <ul className="divide-y divide-[color:var(--color-line)]">
                {demo.findings
                  .filter((finding) => finding.kind === 'room')
                  .map((finding) => {
                    const value = finding.value as unknown as RoomFindingValue;
                    return (
                      <li key={finding.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                        <span>
                          {value.count > 1 ? `${value.count} × ` : ''}
                          {value.name} · {t(`room.size.${value.sizeCode}`)} ·{' '}
                          {t(`room.condition.${value.conditionCode}`)}
                        </span>
                        <span className="text-xs text-[color:var(--color-muted)]">
                          {finding.evidencePhotoIds
                            .map((id) =>
                              t('photoQuotes.photos.label', { position: Number(id.replace('demo-photo-', '')) }),
                            )
                            .join(', ')}
                        </span>
                      </li>
                    );
                  })}
              </ul>
            </div>

            <div className="card">
              <h2 className="mb-2 text-lg font-semibold">{t('photoQuotes.review.priceHeading')}</h2>
              <p className="text-2xl font-semibold">{money(demo.preview.totalCents, demo.preview.currencyCode)}</p>
              <p className="text-sm text-[color:var(--color-muted)]">
                {t('photoQuotes.review.rangeLine', {
                  low: money(demo.preview.range.lowCents, demo.preview.currencyCode),
                  high: money(demo.preview.range.highCents, demo.preview.currencyCode),
                })}
                {demo.preview.estimatedMinutes > 0
                  ? ` · ${t('photoQuotes.review.timeLine', { time: formatMinutes(demo.preview.estimatedMinutes) })}`
                  : ''}
              </p>
              <p className="mt-3 flex items-center gap-2">
                <RiskBadge level={demo.risk.level} />
                <span className="text-sm text-[color:var(--color-muted)]">
                  {t(`photoQuotes.decision.${demo.decision}`)}
                </span>
              </p>
            </div>

            {!hasRead ? (
              <div className="card">
                <h2 className="mb-2 text-lg font-semibold">{t('photoQuotes.demo.upgradeHeading')}</h2>
                <p>{t('photoQuotes.demo.upgradeBody', { starter: PLANS.starter.name, pro: PLANS.pro.name })}</p>
                {user?.role === 'owner' ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {!hasIntake ? (
                      <Link to="/settings#settings-billing" className="btn btn-primary">
                        {t('photoQuotes.demo.tryPlan', { plan: PLANS.starter.name })}
                      </Link>
                    ) : null}
                    <Link
                      to="/settings#settings-billing"
                      className={hasIntake ? 'btn btn-primary' : 'btn btn-secondary'}
                    >
                      {t('photoQuotes.demo.seePlan', { plan: PLANS.pro.name })}
                    </Link>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('photoQuotes.demo.askOwner')}</p>
                )}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </WorkPage>
  );
}
